import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertCircle } from 'lucide-react';
import NewsCard from '../components/NewsCard';
import CategoryFilter from '../components/CategoryFilter';
import RegionFilter from '../components/RegionFilter';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Home() {
  const [breakingNews, setBreakingNews] = useState([]);
  const [trendingNews, setTrendingNews] = useState([]);
  const [latestNews, setLatestNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const [breaking, trending, latest] = await Promise.all([
        axios.get(`${API}/news?breaking=true&limit=5`),
        axios.get(`${API}/news?trending=true&limit=10`),
        axios.get(`${API}/news?limit=30`)
      ]);

      setBreakingNews(breaking.data);
      setTrendingNews(trending.data);
      setLatestNews(latest.data);
    } catch (error) {
      console.error('Failed to fetch news:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="loading-indicator">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055FF] dark:border-[#3377FF] mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Nachrichten werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#09090B] dark:text-[#FAFAFA] mb-4">
            All the world's news.
            <br />
            <span className="text-[#0055FF] dark:text-[#3377FF]">Short. Neutral. Fast.</span>
          </h1>
          <p className="text-lg text-[#52525B] dark:text-[#A1A1AA] max-w-2xl mx-auto">
            Die wichtigsten Nachrichten der Welt in 2-5 Sätzen zusammengefasst.
          </p>
        </motion.div>

        {/* Category Filter */}
        <CategoryFilter />

        {/* Region Filter */}
        <RegionFilter />

        {/* Breaking News Section */}
        {breakingNews.length > 0 && (
          <section className="mb-16" data-testid="breaking-news-section">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B] dark:text-[#FAFAFA] mb-6 flex items-center">
              <AlertCircle className="h-6 w-6 mr-2 text-red-500" />
              Breaking News
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {breakingNews.slice(0, 3).map((news, idx) => (
                <motion.div
                  key={news.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                >
                  <NewsCard news={news} featured={idx === 0} />
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Trending News */}
        {trendingNews.length > 0 && (
          <section className="mb-16" data-testid="trending-news-section">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B] dark:text-[#FAFAFA] mb-6">
              📈 Trending
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trendingNews.slice(0, 6).map((news, idx) => (
                <motion.div
                  key={news.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                >
                  <NewsCard news={news} />
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Latest News */}
        <section data-testid="latest-news-section">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B] dark:text-[#FAFAFA] mb-6">
            🕒 Neueste Nachrichten
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {latestNews.map((news, idx) => (
              <motion.div
                key={news.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
              >
                <NewsCard news={news} />
              </motion.div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default Home;
